import { generateEndingWord } from "client-server-shared/constants";
import { createMarkdownFixer } from "client-server-shared/utils/output-parser";
import { useLoading } from "hooks/use-loading";
import { useMounted } from "hooks/use-mounted";
import { useShallowAbort } from "hooks/use-shallow-abort";
import React from "react";
import { captureException } from "utils/error-catching/lazy-sentry";
import { v4 } from "uuid";

export interface StreamResultItem {
  id: string;
  content: string;
}

interface UseStreamParams {
  setResult: (result: StreamResultItem) => void;
  onHandleError: (error?: Response) => Promise<void> | void;
  shouldFixMarkdown?: boolean;
}

export const useStream = ({
  setResult,
  shouldFixMarkdown,
  onHandleError,
}: UseStreamParams) => {
  const loading = useLoading();
  const streaming = useLoading();

  const mountedVal = useMounted();
  const { mounted } = mountedVal;

  const { onInitRequest, onAbortRequest, getIsAborted, runWithCurrentRequest } =
    useShallowAbort();

  const run = React.useCallback(
    (func: () => void) => {
      mountedVal.safeRun(() => {
        runWithCurrentRequest(() => {
          func();
        });
      });
    },
    [mountedVal, runWithCurrentRequest]
  );

  const onFinishRequest = React.useCallback(() => {
    loading.onIdle();
    streaming.onIdle();
    onAbortRequest();
  }, []);

  const onAbort = React.useCallback(() => {
    onFinishRequest();
  }, [onFinishRequest]);

  React.useEffect(() => {
    return () => {
      onFinishRequest();
    };
  }, []);

  const onError = React.useCallback(
    async (error?: Response) => {
      onFinishRequest();
      await onHandleError(error);
    },
    [onHandleError, onFinishRequest]
  );

  const handleResults = React.useCallback(
    (
      result: StreamResultItem,
      {
        markdownFixer,
        isEnd,
      }: {
        markdownFixer: ReturnType<typeof createMarkdownFixer>;
        isEnd: boolean;
      }
    ) => {
      if (getIsAborted()) {
        loading.onIdle();
        streaming.onIdle();
        return;
      }
      let fixeResult = {
        ...result,
      };
      if (result.content && shouldFixMarkdown) {
        if (isEnd) {
          const { newOutput } = markdownFixer(fixeResult.content, "both");
          fixeResult.content = newOutput;
        } else {
          const { newOutput } = markdownFixer(fixeResult.content, "start");
          fixeResult.content = newOutput;
        }
      }

      setResult(fixeResult);
    },
    [setResult, shouldFixMarkdown]
  );

  const initStream = React.useCallback(
    async (
      makeRequest: () => Promise<Response>,
      beforeStart?: () => void,
      onStreamStart?: () => void,
      onRead?: (chunkVal: string) => void,
      onEnd?: () => void,
      handleError?: () => void
    ) => {
      const markdownFixer = createMarkdownFixer();

      const handleRead = (text: string) => {
        if (onRead) {
          onRead(text);
        }
      };
      if (loading.isLoading) {
        return;
      }

      if (beforeStart) {
        beforeStart();
      }

      loading.onLoading();

      let latestResult: null | StreamResultItem = null;

      try {
        onInitRequest();

        latestResult = {
          id: v4(),
          content: "",
        };

        setResult(latestResult);

        const response = await makeRequest();

        streaming.onLoading();
        if (!response.ok) {
          onError(response);
          if (onEnd) {
            onEnd();
          }
          loading.onIdle();
          streaming.onIdle();
          handleError && handleError();
          return;
        }

        // This data is a ReadableStream
        const data = response.body;

        if (!data) {
          onError();
          handleError && handleError();

          return;
        }

        const reader = data.getReader();
        const decoder = new TextDecoder();
        let done = false;

        let lastMessage = "";

        let hasSavedOnAborted = false;

        let hasMarkedAsStreamStarted = false;

        loading.onIdle();

        while (!done) {
          if (hasSavedOnAborted) {
            done = true;
            onFinishRequest();
            if (onEnd) {
              onEnd();
            }
            break;
          }
          if (!mounted.current) {
            done = true;
            onFinishRequest();
            if (onEnd) {
              onEnd();
            }
            break;
          }

          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          const chunkValue = decoder.decode(value);

          let nextMessage = lastMessage + chunkValue;

          if (nextMessage.includes(generateEndingWord)) {
            handleRead(chunkValue.replace(generateEndingWord, ""));

            done = true;
            lastMessage = nextMessage.replace(generateEndingWord, "");
            latestResult = {
              ...latestResult,
              content: lastMessage,
            };
            handleResults(latestResult, {
              markdownFixer,
              isEnd: true,
            });
            onFinishRequest();
            break;
          }

          handleRead(chunkValue);

          lastMessage = lastMessage + chunkValue;

          if (getIsAborted() && !hasSavedOnAborted) {
            hasSavedOnAborted = true;
            done = true;

            latestResult = {
              ...latestResult,
              content: lastMessage,
            };
            handleResults(latestResult, {
              markdownFixer,
              isEnd: false,
            });
            onFinishRequest();
            if (onEnd) {
              onEnd();
            }
            return;
          }

          run(() => {
            if (latestResult) {
              if (!hasMarkedAsStreamStarted) {
                if (onStreamStart) onStreamStart();
                hasMarkedAsStreamStarted = true;
              }
              latestResult = {
                ...latestResult,
                content: lastMessage,
              };
              handleResults(latestResult, {
                markdownFixer,
                isEnd: false,
              });
            }
          });
        }

        handleResults(latestResult, {
          markdownFixer,
          isEnd: false,
        });
        onFinishRequest();
        if (onEnd) {
          onEnd();
        }
      } catch (e) {
        captureException(e);
        onError();
        handleError && handleError();
        if (onEnd) {
          onEnd();
        }
      }
    },
    [
      streaming.isLoading,
      handleResults,
      onFinishRequest,
      loading.isLoading,
      onError,
    ]
  );

  return {
    initStream,
    streaming: streaming,
    loading: loading,
    onAbortRequest: onAbort,
    onFinishRequest,
  };
};
